import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LabelList,
  AreaChart, Area, CartesianGrid,
} from 'recharts';
import {
  FileText, Clock, CheckCircle, ShoppingCart, Package, Building2, Eye,
  TrendingUp, Timer, Wallet, FolderOpen, PackageCheck, FileEdit,
  AlertTriangle, ArrowRight, SearchCheck, DollarSign, LineChart as LineChartIcon, Coins,
} from 'lucide-react';
import { formatCurrency, getCurrencyMeta } from '../../lib/currencies';

const fmtBRL = (v, moeda = 'BRL') => formatCurrency(v || 0, moeda);
const fmtCompact = (v, moeda = 'BRL') => {
  const n = v || 0;
  const meta = getCurrencyMeta(moeda);
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${meta.symbol} ${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${meta.symbol} ${(n / 1e3).toFixed(1)}K`;
  return formatCurrency(n, moeda);
};

const Section = ({ children, delay = 0, className = '' }) => (
  <div
    className={`animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both ${className}`}
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
    <Icon className="w-4 h-4" /> {children}
  </h3>
);

/* ---------- Badge de variação (MoM / período anterior) ---------- */
const DeltaBadge = ({ delta, label, testId }) => {
  if (delta === null || delta === undefined) return null;
  const up = delta > 0;
  const flat = delta === 0;
  return (
    <span
      data-testid={testId}
      title={label}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
        flat ? 'bg-slate-100 text-slate-600' : up ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
      }`}
    >
      {flat ? '—' : up ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
      <span className="font-normal opacity-70">{label}</span>
    </span>
  );
};

/* ---------- Etapa clicável do funil ---------- */
const FunnelStage = ({ testId, label, value, sub, icon: Icon, palette, onClick }) => {
  const p = {
    slate: ['bg-slate-50 border-slate-200 hover:border-slate-400', 'text-slate-500', 'text-slate-900', 'bg-white text-slate-500 border-slate-200'],
    amber: ['bg-amber-50 border-amber-200 hover:border-amber-400', 'text-amber-700', 'text-amber-900', 'bg-white text-amber-600 border-amber-200'],
    blue: ['bg-sky-50 border-sky-200 hover:border-sky-400', 'text-sky-700', 'text-sky-900', 'bg-white text-sky-600 border-sky-200'],
    green: ['bg-emerald-50 border-emerald-200 hover:border-emerald-400', 'text-emerald-700', 'text-emerald-900', 'bg-white text-emerald-600 border-emerald-200'],
    orange: ['bg-orange-50 border-orange-200 hover:border-orange-400', 'text-orange-700', 'text-orange-900', 'bg-white text-orange-600 border-orange-200'],
    purple: ['bg-violet-50 border-violet-200 hover:border-violet-400', 'text-violet-700', 'text-violet-900', 'bg-white text-violet-600 border-violet-200'],
  }[palette];
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`group flex-1 min-w-0 text-left rounded-xl border p-4 lg:p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500 ${p[0]}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] font-semibold uppercase tracking-wider ${p[1]}`}>{label}</span>
        <span className={`p-1.5 rounded-lg border transition-transform duration-200 group-hover:scale-110 ${p[3]}`}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <p className={`text-4xl font-light tracking-tighter mt-2 ${p[2]}`}>{value}</p>
      <p className={`text-xs mt-1.5 flex items-center gap-1 ${p[1]}`}>
        {sub || <span className="opacity-0">.</span>}
      </p>
      <p className="text-[11px] text-slate-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        Clique para ver a lista →
      </p>
    </button>
  );
};

const FunnelArrow = () => (
  <div className="hidden lg:flex items-center shrink-0 text-slate-300">
    <ArrowRight className="w-5 h-5" />
  </div>
);

/* ---------- Célula de indicador (grid técnico) ---------- */
const IndicatorCell = ({ testId, label, value, sub, icon: Icon, accent, children }) => (
  <div data-testid={testId} className="p-5 lg:p-6 border-slate-200 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:nth-child(-n+3)]:border-r">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${accent}`} />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    </div>
    <p className="text-3xl font-light tracking-tighter text-slate-900 truncate">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    {children}
  </div>
);

/* ---------- Ranking com Recharts ---------- */
const truncate = (s, n = 22) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s);

const RankChart = ({ items, color, moeda = 'BRL' }) => (
  <ResponsiveContainer width="100%" height={Math.max(items.length * 52, 120)}>
    <BarChart data={items} layout="vertical" margin={{ left: 0, right: 70, top: 4, bottom: 4 }}>
      <XAxis type="number" hide />
      <YAxis
        type="category"
        dataKey="nome"
        width={150}
        tickLine={false}
        axisLine={false}
        tick={{ fontSize: 12, fill: '#475569' }}
        tickFormatter={(v) => truncate(v)}
      />
      <Tooltip
        cursor={{ fill: 'rgba(15,23,42,0.04)' }}
        formatter={(v, _n, entry) => [`${fmtBRL(v, moeda)} · ${entry?.payload?.pedidos} pedido(s)`, entry?.payload?.nome]}
        contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
      />
      <Bar dataKey="valor" fill={color} radius={[0, 4, 4, 0]} barSize={18}>
        <LabelList dataKey="valor" position="right" formatter={(v) => fmtCompact(v, moeda)} style={{ fontSize: 11, fill: '#64748b' }} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const RankingCard = ({ testId, title, icon: Icon, iconColor, color, items, emptyText, moeda = 'BRL' }) => (
  <Card data-testid={testId} className="border-slate-200">
    <CardContent className="pt-6">
      <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        {title}
      </h3>
      {(!items || items.length === 0) ? (
        <p className="text-slate-500 text-sm py-6 text-center">{emptyText}</p>
      ) : (
        <RankChart items={items} color={color} moeda={moeda} />
      )}
    </CardContent>
  </Card>
);

/* ---------- Evolução Mensal (AreaChart) ---------- */
const EvolucaoTooltip = ({ active, payload, moeda = 'BRL' }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div
      data-testid="evolucao-tooltip"
      className="rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-2 text-xs"
    >
      <p className="font-semibold text-slate-900 uppercase tracking-wider text-[10px]">{p.label}</p>
      <p className="text-emerald-700 font-medium mt-1">{fmtBRL(p.valor, moeda)}</p>
      <p className="text-slate-500 text-[11px]">{p.count} pedido(s) aprovado(s)</p>
    </div>
  );
};

const EvolucaoMensalChart = ({ evolucao, moeda = 'BRL' }) => {
  if (!evolucao) {
    return (
      <Card className="border-slate-200" data-testid="evolucao-mensal-loading">
        <CardContent className="py-10 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400" />
        </CardContent>
      </Card>
    );
  }
  const { serie = [], total_periodo = 0, total_pedidos = 0, range = {} } = evolucao;
  const temDado = serie.some(x => x.valor > 0);

  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="evolucao-mensal-card">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <LineChartIcon className="w-5 h-5 text-emerald-600" />
            Evolução Mensal — Pedidos Aprovados
          </h3>
          <div className="flex items-baseline gap-4 text-xs text-slate-500">
            <span data-testid="evolucao-meses" className="uppercase tracking-wider font-semibold text-[10px]">
              {range.meses || serie.length} meses
            </span>
            <span data-testid="evolucao-total-pedidos">
              <strong className="text-slate-900 font-medium">{total_pedidos}</strong> pedido(s)
            </span>
            <span data-testid="evolucao-total-valor">
              <strong className="text-emerald-700 font-medium">{fmtBRL(total_periodo, moeda)}</strong> no período
            </span>
          </div>
        </div>

        {!temDado ? (
          <div className="py-12 text-center text-slate-500 text-sm" data-testid="evolucao-empty">
            Nenhum pedido aprovado no período selecionado.
          </div>
        ) : (
          <div data-testid="evolucao-chart">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={serie} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-evolucao" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => fmtCompact(v, moeda)}
                  width={70}
                />
                <Tooltip content={<EvolucaoTooltip moeda={moeda} />} cursor={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '3 3' }} />
                <Area
                  type="monotone"
                  dataKey="valor"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  fill="url(#grad-evolucao)"
                  activeDot={{ r: 5, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function DashboardCompras({ data, evolucao, itensUrgentes, onVerItem, onNavigate, moeda = 'BRL', onMoedaChange }) {
  if (!data) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="dashboard-compras-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const { cotacoes, pedidos, indicadores } = data;
  const moedasDisponiveis = (data.moedas_disponiveis && data.moedas_disponiveis.length) ? data.moedas_disponiveis : ['BRL'];
  // Garante que a moeda selecionada exista nos dados
  const moedaAtiva = moedasDisponiveis.includes(moeda) ? moeda : moedasDisponiveis[0];
  const pm = (data.por_moeda && data.por_moeda[moedaAtiva]) || {};
  // Valores monetários (por moeda, nunca somando moedas diferentes)
  const valorAprovados = pm.valor_aprovados ?? 0;
  const valorTotal = pm.valor_total ?? 0;
  const ticketMedio = pm.ticket_medio ?? 0;
  const valorPendente = pm.valor_pendente_pagamento ?? 0;
  const valorPago = pm.valor_pago ?? 0;
  const comparativo = pm.comparativo || data.comparativo;
  const topFornecedores = pm.top_fornecedores || [];
  const comprasPorProjeto = pm.compras_por_projeto || [];

  const totalFinanceiro = valorPago + valorPendente;
  const pctPago = totalFinanceiro > 0 ? (valorPago / totalFinanceiro) * 100 : 0;
  const pctAprovados = valorTotal > 0 ? (valorAprovados / valorTotal) * 100 : 0;

  const multiMoeda = moedasDisponiveis.length > 1;

  return (
    <div className="space-y-8" data-testid="dashboard-compras">

      {/* ===== Seletor de moeda (valores nunca somam moedas diferentes) ===== */}
      {multiMoeda && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-moeda-selector">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
            <Coins className="w-4 h-4" /> Moeda:
          </span>
          {moedasDisponiveis.map((code) => {
            const m = getCurrencyMeta(code);
            const active = code === moedaAtiva;
            return (
              <button
                key={code}
                type="button"
                data-testid={`dashboard-moeda-${code}`}
                onClick={() => onMoedaChange?.(code)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                }`}
              >
                {m.symbol} {code}
              </button>
            );
          })}
          <span className="text-[11px] text-slate-400 ml-1">valores exibidos apenas na moeda selecionada</span>
        </div>
      )}

      {/* ===== Câmbio gerencial: Total Geral Equivalente em R$ ===== */}
      {multiMoeda && data.cambio_gerencial && (
        <div
          data-testid="cambio-gerencial-card"
          className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-4 lg:p-5 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3">
            <span className="p-2 rounded-lg bg-indigo-600 text-white">
              <Coins className="w-5 h-5" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600">Total Geral Equivalente (gerencial)</p>
              <p className="text-2xl font-light tracking-tighter text-slate-900" data-testid="cambio-total-equivalente">
                {fmtBRL(data.cambio_gerencial.total_equivalente_brl, 'BRL')}
              </p>
              <p className="text-[11px] text-slate-400">todas as moedas convertidas em R$ pela taxa manual de cada pedido</p>
            </div>
          </div>
          {data.cambio_gerencial.pedidos_sem_taxa > 0 && (
            <div
              className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 max-w-sm"
              data-testid="cambio-aviso-sem-taxa"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                <strong>{data.cambio_gerencial.pedidos_sem_taxa}</strong> pedido(s) em moeda estrangeira sem taxa de câmbio definida
                ({fmtBRL(data.cambio_gerencial.valor_sem_taxa, 'BRL')} não incluídos). Defina a taxa no detalhe do pedido.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ===== Funil de Cotações ===== */}
      <Section delay={0}>
        <div className="flex items-center justify-between mb-3">
          <SectionTitle icon={FileText}>Funil de Cotações</SectionTitle>
          <button
            type="button"
            data-testid="funil-total-cotacoes"
            onClick={() => onNavigate('cotacoes', [])}
            className="flex items-baseline gap-2 rounded-lg px-3 py-1 hover:bg-slate-100 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
          >
            <span className="text-2xl font-light tracking-tighter text-slate-900">{cotacoes.total}</span>
            <span className="text-xs text-slate-500">cotações no período</span>
          </button>
        </div>
        <div className="flex flex-col lg:flex-row gap-3 items-stretch">
          <FunnelStage
            testId="funil-rascunhos"
            label="Em Rascunho" value={cotacoes.rascunhos} icon={FileEdit} palette="slate"
            onClick={() => onNavigate('cotacoes', ['rascunho'])}
          />
          <FunnelArrow />
          <FunnelStage
            testId="funil-aguardando"
            label="Aguardando Fornecedor" value={cotacoes.aguardando_fornecedor} icon={Clock} palette="amber"
            onClick={() => onNavigate('cotacoes', ['aguardando_respostas'])}
          />
          <FunnelArrow />
          <FunnelStage
            testId="funil-analisando"
            label="Em Análise" value={cotacoes.analisando} icon={SearchCheck} palette="blue"
            sub={cotacoes.em_aberto > 0 ? `${cotacoes.em_aberto} em aberto no total` : null}
            onClick={() => onNavigate('cotacoes', ['analisando', 'aprovada_parcial'])}
          />
          <FunnelArrow />
          <FunnelStage
            testId="funil-aprovadas"
            label="Aprovadas" value={cotacoes.aprovadas} icon={CheckCircle} palette="green"
            sub={
              <span className={cotacoes.aprovadas_sem_pedido > 0 ? 'font-semibold text-emerald-800' : ''}>
                {cotacoes.aprovadas_sem_pedido} sem pedido gerado
              </span>
            }
            onClick={() => onNavigate('cotacoes', ['aprovada', 'aprovada_parcial'])}
          />
        </div>
      </Section>

      {/* ===== Pedidos & Financeiro ===== */}
      <Section delay={80}>
        <SectionTitle icon={ShoppingCart}>Pedidos & Recebimento</SectionTitle>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Pipeline de pedidos */}
          <div className="lg:col-span-2 flex flex-col sm:flex-row gap-3 items-stretch">
            <FunnelStage
              testId="ped-total"
              label="Total de Pedidos" value={pedidos.total} icon={ShoppingCart} palette="blue"
              sub={comparativo?.pedidos?.delta_pct != null
                ? `${comparativo.pedidos.delta_pct > 0 ? '▲' : comparativo.pedidos.delta_pct < 0 ? '▼' : '—'} ${Math.abs(comparativo.pedidos.delta_pct).toFixed(1)}% ${comparativo.label}`
                : null}
              onClick={() => onNavigate('pedidos', [])}
            />
            <FunnelArrow />
            <FunnelStage
              testId="ped-aguardando"
              label="Aguardando Recebimento" value={pedidos.aguardando_recebimento} icon={Clock} palette="orange"
              sub={pedidos.recebidos_parcial > 0 ? `+${pedidos.recebidos_parcial} parcial(is)` : null}
              onClick={() => onNavigate('pedidos', ['pendente', 'aprovado', 'enviado'])}
            />
            <FunnelArrow />
            <FunnelStage
              testId="ped-recebidos"
              label="Recebidos em Estoque" value={pedidos.recebidos} icon={PackageCheck} palette="purple"
              sub={`${pedidos.recebidos_parcial} com recebimento parcial`}
              onClick={() => onNavigate('pedidos', ['recebido', 'recebido_parcial'])}
            />
          </div>

          {/* Hero financeiro */}
          <div
            data-testid="hero-valor-aprovados"
            className="rounded-xl bg-slate-900 text-white p-5 lg:p-6 flex flex-col justify-between transition-all duration-200 hover:shadow-xl"
          >
            <div>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Valor Pedidos Aprovados</span>
                <span className="p-1.5 rounded-lg bg-slate-800 text-emerald-400">
                  <DollarSign className="w-4 h-4" />
                </span>
              </div>
              <p className="text-3xl xl:text-4xl font-light tracking-tighter mt-2 text-emerald-400">
                {fmtBRL(valorAprovados, moedaAtiva)}
              </p>
              <div className="mt-2">
                <DeltaBadge
                  delta={comparativo?.valor_aprovados?.delta_pct}
                  label={comparativo?.label}
                  testId="delta-valor-aprovados"
                />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                <span>do total em compras</span>
                <span>{fmtBRL(valorTotal, moedaAtiva)}</span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-400 rounded-full transition-all duration-700" style={{ width: `${pctAprovados}%` }} />
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== Evolução Mensal ===== */}
      <Section delay={120}>
        <EvolucaoMensalChart evolucao={evolucao} moeda={moedaAtiva} />
      </Section>

      {/* ===== Indicadores Gerenciais ===== */}
      <Section delay={160}>
        <SectionTitle icon={TrendingUp}>Indicadores Gerenciais</SectionTitle>
        <Card className="border-slate-200 overflow-hidden" data-testid="indicadores-gerenciais">
          <div className="grid grid-cols-1 md:grid-cols-4">
            <IndicatorCell
              testId="kpi-ticket-medio"
              label="Ticket Médio / Pedido" value={fmtBRL(ticketMedio, moedaAtiva)}
              icon={Wallet} accent="text-teal-600"
            />
            <IndicatorCell
              testId="kpi-taxa-conversao"
              label="Conversão Cotação → Pedido" value={`${indicadores.taxa_conversao}%`}
              icon={TrendingUp} accent="text-sky-600"
            >
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-3">
                <div className="h-full bg-sky-500 rounded-full transition-all duration-700" style={{ width: `${Math.min(indicadores.taxa_conversao, 100)}%` }} />
              </div>
            </IndicatorCell>
            <IndicatorCell
              testId="kpi-tempo-ciclo"
              label="Tempo Médio de Ciclo"
              value={indicadores.tempo_medio_ciclo_dias !== null ? `${indicadores.tempo_medio_ciclo_dias} dias` : '—'}
              sub="da cotação ao pedido gerado"
              icon={Timer} accent="text-violet-600"
            />
            <IndicatorCell
              testId="kpi-pendente-pagamento"
              label="Pendente de Pagamento" value={fmtBRL(valorPendente, moedaAtiva)}
              sub={`Pago: ${fmtBRL(valorPago, moedaAtiva)}`}
              icon={AlertTriangle} accent="text-red-500"
            >
              {totalFinanceiro > 0 && (
                <div className="h-1.5 bg-red-100 rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-700" style={{ width: `${pctPago}%` }} />
                </div>
              )}
            </IndicatorCell>
          </div>
        </Card>
      </Section>

      {/* ===== Rankings ===== */}
      <Section delay={240} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RankingCard
          testId="ranking-fornecedores"
          title="Top Fornecedores por Valor"
          icon={Building2} iconColor="text-sky-600" color="#0ea5e9"
          items={topFornecedores}
          moeda={moedaAtiva}
          emptyText="Nenhum fornecedor com pedidos no período"
        />
        <RankingCard
          testId="ranking-projetos"
          title="Compras por Projeto"
          icon={FolderOpen} iconColor="text-teal-600" color="#14b8a6"
          items={comprasPorProjeto}
          moeda={moedaAtiva}
          emptyText="Nenhum pedido vinculado a projeto no período"
        />
      </Section>

      {/* ===== Itens Urgentes ===== */}
      <Section delay={320}>
        <Card data-testid="card-itens-urgentes" className="border-slate-200">
          <CardContent className="pt-6">
            <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Itens Urgentes
              {itensUrgentes.length > 0 && (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{itensUrgentes.length}</Badge>
              )}
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {itensUrgentes.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-emerald-200 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">Nenhum item urgente pendente</p>
                </div>
              ) : (
                itensUrgentes.map((item, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => onVerItem(item)}
                    data-testid={`btn-ver-urgente-${item.numero}`}
                    className="w-full flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-100 text-left transition-all duration-200 hover:border-red-300 hover:shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-400"
                  >
                    <span className="p-1.5 rounded-lg bg-red-100 text-red-600 shrink-0">
                      {item.tipo === 'cotacao' ? <FileText className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {item.numero} — {item.titulo}
                      </p>
                      <p className="text-xs text-slate-500">
                        {item.tipo === 'cotacao' ? 'Cotação' : 'Pedido'} • {item.status}
                      </p>
                    </div>
                    <Eye className="w-4 h-4 text-slate-400 shrink-0" />
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </Section>
    </div>
  );
}
